import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { pillarStyle, normalizeSteps, deriveStatus, STATUS_META } from "@/lib/planiq";
import { Compass, ListChecks, Question, Check } from "@phosphor-icons/react";
import CardRefine from "@/components/CardRefine";

export default function CardModal({ card, pillar, open, onClose, onToggleStep, planId, onCardsUpdate, lockReason }) {
  if (!card) return null;
  const style = pillarStyle(pillar);
  const steps = normalizeSteps(card.steps);
  const status = deriveStatus(steps);
  const meta = STATUS_META[status];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-gray-300 max-w-2xl p-0 gap-0 max-h-[88vh] overflow-y-auto"
        data-testid="card-detail-modal">
        <DialogTitle className="sr-only">{card.title}</DialogTitle>
        <DialogDescription className="sr-only">Action item detail and execution guide</DialogDescription>
        <div className="h-1.5" style={{ backgroundColor: style.hex }} />
        <div className="p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold border"
              style={{ color: style.hex, borderColor: style.hex, backgroundColor: style.bg }}>
              <span className="w-2 h-2" style={{ backgroundColor: style.hex }} />
              {pillar?.name || "Unassigned"}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-wider text-gray-400">
              {card.posture_tag}
            </span>
          </div>

          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight mb-7">
            {card.title}
          </h2>

          <section className="mb-6">
            <h3 className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">
              <Compass size={16} weight="duotone" /> Justification
            </h3>
            <p className="text-base text-gray-700 leading-relaxed">{card.justification}</p>
          </section>

          <section className="mb-7">
            <h3 className="flex items-center justify-between text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-3">
              <span className="flex items-center gap-2"><ListChecks size={16} weight="duotone" /> Action Steps</span>
              {steps.length > 0 && (
                <span className="font-mono normal-case tracking-normal" data-testid="modal-step-progress">
                  {steps.filter((s) => s.done).length}/{steps.length} done
                </span>
              )}
            </h3>
            {steps.length > 0 ? (
              <ul className="space-y-2.5" data-testid="card-steps">
                {steps.map((s, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <button
                      onClick={() => onToggleStep && onToggleStep(card._id, i)}
                      data-testid={`modal-step-toggle-${i}`}
                      aria-label={s.done ? "Mark incomplete" : "Mark complete"}
                      className={`shrink-0 mt-0.5 w-5 h-5 border flex items-center justify-center transition-colors ${s.done ? "bg-gray-950 border-gray-950" : "border-gray-300 hover:border-gray-900"}`}
                    >
                      {s.done && <Check size={13} weight="bold" className="text-white" />}
                    </button>
                    <span className={`text-sm leading-relaxed pt-0.5 ${s.done ? "line-through text-gray-400" : "text-gray-800"}`}>
                      {s.text}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{card.execution_guide}</p>
            )}
          </section>

          {card.diagnostic_questions && card.diagnostic_questions.length > 0 && (
            <section className="mb-7">
              <h3 className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-3">
                <Question size={16} weight="duotone" /> Diagnostic Questions
              </h3>
              <ul className="space-y-1.5" data-testid="card-diagnostic-questions">
                {card.diagnostic_questions.map((q, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-700 leading-relaxed">
                    <span className="shrink-0 mt-2 w-1.5 h-1.5" style={{ backgroundColor: style.hex }} />
                    <span className="italic">{q}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">Status</h3>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full"
                style={{ color: meta.hex, backgroundColor: meta.bg }} data-testid="card-status-display">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.hex }} />
                {meta.label}
              </span>
              <span className="text-xs text-gray-400 font-mono">
                Updates automatically as you check off sub-tasks.
              </span>
            </div>
          </section>

          {planId && onCardsUpdate && (
            <CardRefine planId={planId} card={card} onCardsUpdate={onCardsUpdate} lockReason={lockReason} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
